import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { TowerControl, Headphones, Download, User, Briefcase, Sparkles, Menu, X } from "lucide-react";
import TranslatePage from "../TranslatePage";
import { useAuth } from "../../context/AuthContext";

/* IndianAirports.com tenant shell — its own header, nav and footer (mockup-based).
   Rendered for all /india/airports* routes; TravelerPulse chrome is hidden there. */

const NAV = [
  ["Home", "/india/airports", true],
  ["Airports", "/india/airports/directory"],
  ["Airlines", "/india/airlines"],
  ["My Flight", "/india/my-flight"],
  ["Flights", "/flight-search"],
  ["Destinations", "/destinations"],
  ["Transport", "/ground"],
  ["Stay", "/stays"],
  ["News", "/travel-news"],
  ["Guides", "/guides"],
  ["Community", "/community"],
  ["About", "/india/about"],
];

export default function IALayout() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const listen = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (synth.speaking) { synth.cancel(); return; }
    const text = document.querySelector("main")?.innerText?.slice(0, 3000) || "";
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.95;
    // Traveler's chosen voice (picked in My Social Pulse briefing settings)
    try {
      const p = JSON.parse(localStorage.getItem("tp_tts") || "{}");
      if (p.voiceURI) {
        const v = synth.getVoices().find((x) => x.voiceURI === p.voiceURI);
        if (v) u.voice = v;
      }
      if (p.rate) u.rate = p.rate;
    } catch { /* default voice */ }
    synth.speak(u);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-40" data-testid="ia-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-5 py-2.5 flex items-center gap-2 sm:gap-3">
          <Link to="/india/airports" className="flex items-center gap-2.5 shrink-0 min-w-0" data-testid="ia-logo">
            <span className="inline-flex p-2 rounded-xl bg-blue-700 text-white shrink-0"><TowerControl className="w-5 h-5" /></span>
            <span className="min-w-0">
              <span className="font-heading font-900 text-lg sm:text-xl leading-none whitespace-nowrap"><span className="text-blue-700">Indian</span><span className="text-orange-600">Airports</span><span className="text-zinc-400 text-sm hidden sm:inline">.com</span></span>
              <span className="hidden sm:block text-[10px] text-zinc-500 font-600">India Begins at the Airport</span>
            </span>
          </Link>
          <div className="ml-auto flex items-center gap-0.5 sm:gap-1.5 text-xs min-w-0 shrink">
            <span className="hidden md:block" data-testid="ia-translate"><TranslatePage /></span>
            <button onClick={listen} data-testid="ia-listen-btn" className="hidden sm:inline-flex items-center gap-1 font-700 text-zinc-600 hover:text-blue-700 px-2 py-1.5"><Headphones className="w-3.5 h-3.5" /> Listen</button>
            <Link to="/get-app" data-testid="ia-download-app" className="hidden sm:inline-flex items-center gap-1 font-700 text-zinc-600 hover:text-blue-700 px-2 py-1.5"><Download className="w-3.5 h-3.5" /> Download App</Link>
            {user ? (
              <Link to="/travel-hub?ref=indianairports&src=%2Fia-header" data-testid="ia-account"
                className="inline-flex items-center gap-1 font-700 text-blue-700 hover:text-blue-800 px-1.5 sm:px-2 py-1.5">
                <span className="w-5 h-5 rounded-full bg-blue-700 text-white text-[10px] font-800 inline-flex items-center justify-center">
                  {(user.name || user.email || "T")[0].toUpperCase()}
                </span>
                <span className="hidden sm:inline max-w-[90px] truncate">{(user.name || user.email || "").split(" ")[0].split("@")[0]}</span>
              </Link>
            ) : (
              <Link to="/login" data-testid="ia-signin" className="inline-flex items-center gap-1 font-700 text-zinc-600 hover:text-blue-700 px-1.5 sm:px-2 py-1.5"><User className="w-3.5 h-3.5" /><span className="hidden sm:inline">Sign In</span></Link>
            )}
            <Link to="/travel-hub" data-testid="ia-mth-btn" className="inline-flex items-center gap-1.5 whitespace-nowrap bg-blue-700 text-white font-800 rounded-lg px-2.5 sm:px-3 py-2 hover:bg-blue-800"><Briefcase className="w-3.5 h-3.5" /><span className="hidden sm:inline">MY TRAVEL HUB</span><span className="sm:hidden">HUB</span></Link>
            <button onClick={() => setOpen((v) => !v)} className="md:hidden p-1.5 text-zinc-600 shrink-0" data-testid="ia-nav-toggle">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          </div>
        </div>
        <nav className={`${open ? "block" : "hidden"} md:block border-t border-zinc-100`} data-testid="ia-nav">
          <div className="max-w-7xl mx-auto px-5 flex flex-col md:flex-row md:items-center gap-0.5 md:gap-1 py-1 overflow-x-auto">
            {NAV.map(([label, to, exact]) => (
              <NavLink key={label} to={to} end={!!exact} onClick={() => setOpen(false)} data-testid={`ia-nav-${label.toLowerCase()}`}
                className={({ isActive }) => `shrink-0 text-[11px] font-800 uppercase tracking-wide px-2.5 py-2 rounded-md transition-colors ${isActive && to.startsWith("/india") ? "text-blue-700 bg-blue-50" : "text-zinc-600 hover:text-blue-700 hover:bg-zinc-50"}`}>
                {label}
              </NavLink>
            ))}
            <button onClick={() => { setOpen(false); window.dispatchEvent(new Event("tp:ask-open")); }} data-testid="ia-nav-ai"
              className="shrink-0 text-[11px] font-800 uppercase tracking-wide px-2.5 py-2 rounded-md text-violet-700 hover:bg-violet-50 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> AI Assistant
            </button>
          </div>
        </nav>
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="border-t border-zinc-200 bg-zinc-50 mt-10" data-testid="ia-footer">
        <div className="max-w-7xl mx-auto px-5 py-8 flex flex-wrap items-start gap-8">
          <div className="max-w-xs">
            <div className="font-heading font-900 text-lg"><span className="text-blue-700">Indian</span><span className="text-orange-600">Airports</span><span className="text-zinc-400 text-xs">.com</span></div>
            <p className="text-xs text-zinc-500 mt-1">Every Airport. Every Journey. One India Aviation Platform.</p>
            <p className="text-[11px] text-zinc-400 mt-2">Powered by <Link to="/" className="font-700 text-blue-700 hover:underline">TravelerPulse.pro</Link></p>
          </div>
          <div className="text-xs space-y-1.5">
            <div className="font-800 uppercase text-[10px] text-zinc-400 tracking-wide">Explore</div>
            <Link to="/india/airports/directory" className="block text-zinc-600 hover:text-blue-700">Find an Airport</Link>
            <Link to="/india/guides" className="block text-zinc-600 hover:text-blue-700">Travel to India Guides</Link>
            <Link to="/india/guides/oci-nri-rules" className="block text-zinc-600 hover:text-blue-700">OCI & NRI Rules</Link>
            <Link to="/india/partners" className="block text-zinc-600 hover:text-blue-700">Embed Our Widget</Link>
            <Link to="/airlines" className="block text-zinc-600 hover:text-blue-700">Airlines</Link>
            <Link to="/travel-news" className="block text-zinc-600 hover:text-blue-700">Aviation News</Link>
          </div>
          <div className="text-xs space-y-1.5">
            <div className="font-800 uppercase text-[10px] text-zinc-400 tracking-wide">Plan</div>
            <Link to="/plan-ai" className="block text-zinc-600 hover:text-blue-700">AI Trip Planner</Link>
            <Link to="/travel-hub" className="block text-zinc-600 hover:text-blue-700">My Travel Hub</Link>
            <Link to="/stays" className="block text-zinc-600 hover:text-blue-700">Hotels</Link>
          </div>
          <p className="text-[11px] text-zinc-400 ml-auto max-w-[240px]">Airport information changes — profiles carry a source and last-verified date. Always confirm critical details with your airline.</p>
        </div>
      </footer>
    </div>
  );
}
